import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import "./DialogAlert.scss";

type AlertOptions = {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
};

type AlertState = AlertOptions & {
    resolve: (value: boolean) => void;
    showCancel: boolean;
};

type DialogAlertContextType = {
    show: (options: AlertOptions, showCancel: boolean) => Promise<boolean>;
};

const DialogAlertContext = createContext<DialogAlertContextType | null>(null);

let globalShow: DialogAlertContextType["show"] | null = null;

function DialogAlertProvider({ children }: { children: ReactNode }) {
    const [alertState, setAlertState] = useState<AlertState | null>(null);

    const show = useCallback((options: AlertOptions, showCancel: boolean): Promise<boolean> => {
        return new Promise((resolve) => {
            setAlertState({ ...options, resolve, showCancel });
        });
    }, []);

    globalShow = show;

    const handleConfirm = () => {
        alertState?.resolve(true);
        setAlertState(null);
    };

    const handleCancel = () => {
        alertState?.resolve(false);
        setAlertState(null);
    };

    return (
        <DialogAlertContext.Provider value={{ show }}>
            {children}
            {alertState && (
                <div className="DialogAlertOverlay" onClick={handleCancel}>
                    <div className="DialogAlertBox" onClick={(e) => e.stopPropagation()}>
                        {alertState.title && (
                            <h2 className="DialogAlertTitle">{alertState.title}</h2>
                        )}
                        <p className="DialogAlertMessage">{alertState.message ?? ""}</p>
                        <div className="DialogAlertButtons">
                            <button
                                type="button"
                                className="DialogAlertButton DialogAlertButtonConfirm"
                                onClick={handleConfirm}
                            >
                                {alertState.confirmText || "확인"}
                            </button>
                            {alertState.showCancel && (
                                <button
                                    type="button"
                                    className="DialogAlertButton DialogAlertButtonCancel"
                                    onClick={handleCancel}
                                >
                                    {alertState.cancelText || "취소"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </DialogAlertContext.Provider>
    );
}

function useDialogAlert() {
    const context = useContext(DialogAlertContext);
    if (!context) {
        throw new Error("useDialogAlert must be used within DialogAlert.Context");
    }
    return context;
}

// Static methods for global access
async function alert(title: string, message?: string): Promise<void> {
    if (!globalShow) {
        throw new Error("DialogAlert.Context is not mounted");
    }
    await globalShow({ title, message }, false);
}

async function confirm(title: string, message?: string): Promise<boolean> {
    if (!globalShow) {
        throw new Error("DialogAlert.Context is not mounted");
    }
    return globalShow({ title, message }, true);
}

async function custom(options: AlertOptions & { showCancel?: boolean }): Promise<boolean> {
    if (!globalShow) {
        throw new Error("DialogAlert.Context is not mounted");
    }
    const { showCancel = false, ...rest } = options;
    return globalShow(rest, showCancel);
}

const DialogAlert = {
    Context: DialogAlertProvider,
    useDialogAlert,
    alert,
    confirm,
    custom,
};

export default DialogAlert;
